/**
 * The Career's hangar (M6 plan P21; D-TBD: the flown ship is not a worker agent): the loadouts and insurance of
 * the fleet ships the player owns, kept in the optional `career.hangar` (owned fleet ships) beside the worker,
 * which owns everything else about them (agent, order, captain, cargo, position).
 *
 * - **Entries** are `OwnedShip`s keyed by fleet id (`ps-<seq>`). `OWN_SHIP_ID` is reserved for the Career's own
 *   (M2 lease) ship when P24's switch stows it; that entry's insurance is `lease.insured`, never its own flag.
 * - **The flown ship** is `career.flownShip` (absent = the lease ship). While flown, `career.ship` is the truth for
 *   the fit; the entry keeps its insurance and escape pod, and P24's switch writes the loadout back on leaving.
 * - **Salvaged provenance travels with the ship** (economy plan M6I-5, R3-H1): `ownedFromShip` copies the active
 *   `ShipState.salvaged` and `loadoutOf` restores it. A salvaged module stays salvaged: it is never turned into a
 *   plain `fitted`/`locker` entry (that would resell it at `sellFraction` × the module price, the D2-FC #7 exploit).
 * - **A claimed hull's condition travels with the hull** (M9 CH-8): `flaws` and `valueMult` are the hull's, not
 *   the loadout's. `newOwnedShip` takes them for a registered claimed ship, and `ownedFromShip` copies them from
 *   the entry being rewritten (the switch passes it), so leaving and reboarding a ship keeps them. `loadoutOf` does
 *   not carry them: the flown ship's entry stays in the hangar, and its flaws are read from there (`flownEntry`).
 *   Both are written only when not pristine (flaws 0, valueMult 1), so a bought ship's entry is unchanged.
 * - **A registered claim** (M9 CH-7s, `addClaimedShip`) adds the claimed ship's entry and finishes the hulk's key
 *   in one Career write. The entry carries no "claimed" flag (the strict `ownedShipSchema` would need a key): the
 *   worker knows a claimed ship (`hulk:<key>` world flag) and refuses `scrapClaimedShip` for any other.
 *
 * - **The stock Mk** (M10 P5a5a; hull tiers spec §3.2, §6.1): a new entry's fit is the given stock fit with each
 *   hull-rated module (cargo, shield, power: the slots a Mk fraction scales, `hullFit.ts RatedSlot`) at the hull row's
 *   `stockFit.mk`, else at its band's `hull-lines.json sizes.<band>.stockMk` (L/XL: Mk II)
 *   **only for a row with a `fit`**, else the stock fit unchanged. No shipped row sets `fit` or `stockFit` until W6,
 *   so every new entry (the Danube's too) is today's.
 *
 * - **Names and the hull history** (M10 P5a3b): `OwnedShip.name` mirrors the worker's names column when the player
 *   named the ship (at purchase or by `renameShip`); absent = the worker's default name. `recordHullEvent` appends
 *   one event (bought, renamed, boarded, repaired, claimed), keeping at most `HULL_HISTORY_MAX`, the oldest dropped.
 *
 * Every function is a pure Career command: the input is never changed.
 */
import { z } from 'zod'
import { moduleId, parseModuleId, type ModuleId, type ModuleMk, type SlotId } from '../data/m2Schemas'
import hullsJson from '../data/strategic/hulls.json'
import assetsJson from '../data/assets.json'
import shipsJson from '../data/ships.json'
import { assetsFileSchema } from '../data/assetSchema'
import { cradleSocketOf, type CradleData } from '../docking/cradle'
import { hullOfDef, type RatedSlot } from '../career/hullFit'
import { fail, ok, type CmdResult } from '../career/result'
import { HULL_HISTORY_MAX, SHIP_NAME_MAX_CHARS, type Career, type HullEvent, type OwnedShip, type SalvagedState, type ShipState } from '../career/types'
import { HULL_BAND_IDS, loadHullLines, type HullBand } from '../strategic/hullLines'
import { hullStockFitSchema } from '../strategic/schemas'

/** The reserved hangar id of the Career's own (M2 lease) ship. */
export const OWN_SHIP_ID = 'own'

const EMPTY: readonly OwnedShip[] = Object.freeze([])

/** The hangar (empty when the Career has none yet). */
export function hangarOf(career: Career): readonly OwnedShip[] {
  return career.hangar ?? EMPTY
}

/** The hangar entry for `fleetId`, or null. */
export function ownedShip(career: Career, fleetId: string): OwnedShip | null {
  const h = career.hangar
  if (h === undefined) return null
  for (const s of h) if (s.fleetId === fleetId) return s
  return null
}

/** The hangar entry of the ship the player flies, or null for the lease ship (or a dangling `flownShip`). */
export function flownEntry(career: Career): OwnedShip | null {
  const id = career.flownShip
  return id === undefined || id === OWN_SHIP_ID ? null : ownedShip(career, id)
}

/** M9 (CH-8): a claimed hull's condition, kept on its hangar entry. Absent fields = pristine (no flaws, value × 1). */
export type HullCondition = Pick<OwnedShip, 'flaws' | 'valueMult'>

/** Writes `cond` into `out` (only the non-pristine fields) and returns it. */
function withCondition(out: OwnedShip, cond: HullCondition | null | undefined): OwnedShip {
  if (cond === null || cond === undefined) return out
  if (cond.flaws !== undefined && cond.flaws !== 0) out.flaws = cond.flaws
  if (cond.valueMult !== undefined && cond.valueMult !== 1) out.valueMult = cond.valueMult
  return out
}

function copySalvaged(s: SalvagedState): SalvagedState {
  return { locker: s.locker.map((m) => ({ ...m })), fitted: Object.fromEntries(Object.entries(s.fitted).map(([k, m]) => [k, { ...m }])) }
}

/**
 * A hangar entry from a ship state (stowing the flown ship, or a newly delivered one). `salvaged` moves with it
 * (M6I-5) and is omitted when there is none, so a plain ship's entry stays as small as an old one. `cond` is the
 * condition the ship's entry had (M9 CH-8: the entry being rewritten), kept as it was.
 */
export function ownedFromShip(
  ship: Pick<ShipState, 'defId' | 'fitted' | 'locker' | 'salvaged' | 'resonanceMeter'>,
  fleetId: string,
  insured: boolean,
  escapePod: boolean,
  cond?: HullCondition | null,
): OwnedShip {
  const out: OwnedShip = { fleetId, defId: ship.defId, fitted: { ...ship.fitted }, locker: [...ship.locker], insured, escapePod }
  if (ship.salvaged !== undefined && (ship.salvaged.locker.length > 0 || Object.keys(ship.salvaged.fitted).length > 0)) out.salvaged = copySalvaged(ship.salvaged)
  if (ship.resonanceMeter === true) out.resonanceMeter = true
  return withCondition(out, cond)
}

/** The loadout boarding an entry puts into `career.ship` (P24's switch); `salvaged` comes back with it (M6I-5). */
export function loadoutOf(entry: OwnedShip): Pick<ShipState, 'defId' | 'fitted' | 'locker' | 'salvaged' | 'resonanceMeter'> {
  const out: Pick<ShipState, 'defId' | 'fitted' | 'locker' | 'salvaged' | 'resonanceMeter'> = { defId: entry.defId, fitted: { ...entry.fitted }, locker: [...entry.locker] }
  if (entry.salvaged !== undefined) out.salvaged = copySalvaged(entry.salvaged)
  if (entry.resonanceMeter === true) out.resonanceMeter = true
  return out
}

/** The fields the stock Mk reads of a hull row (validated; the rest of the row is ignored). */
const stockRowSchema = z.object({
  id: z.string(),
  band: z.enum(HULL_BAND_IDS).optional(),
  fit: z.unknown().optional(),
  stockFit: hullStockFitSchema.optional(),
})

const MK_OF: Readonly<Record<number, ModuleMk>> = { 1: 1, 2: 2, 3: 3 }

/** M10 P5a5a: what the stock Mk reads: each hull's `stockFit.mk` and whether it has a `fit`, and each band's `stockMk`. */
export interface StockMkData {
  hulls: ReadonlyMap<string, { band?: HullBand; rated: boolean; mk?: ModuleMk }>
  stockMk: Readonly<Partial<Record<HullBand, ModuleMk>>>
}

/** Builds the stock-Mk data from raw `hulls.json` rows and parsed `hull-lines.json` sizes (tests pass copies). */
export function stockMkDataFrom(rawHulls: readonly unknown[], sizes: Readonly<Record<HullBand, { stockMk?: number }>>): StockMkData {
  const hulls = new Map<string, { band?: HullBand; rated: boolean; mk?: ModuleMk }>()
  for (const raw of rawHulls) {
    const row = stockRowSchema.parse(raw)
    const entry: { band?: HullBand; rated: boolean; mk?: ModuleMk } = { rated: row.fit !== undefined }
    if (row.band !== undefined) entry.band = row.band
    if (row.stockFit !== undefined) entry.mk = MK_OF[row.stockFit.mk]
    hulls.set(row.id, entry)
  }
  const stockMk: Partial<Record<HullBand, ModuleMk>> = {}
  for (const b of HULL_BAND_IDS) {
    const mk = sizes[b].stockMk
    if (mk !== undefined) stockMk[b] = MK_OF[mk]
  }
  return { hulls, stockMk }
}

let shippedStock: StockMkData | null = null

/** The shipped stock-Mk data (`hulls.json`, `hull-lines.json`; parsed once). */
export function stockMkData(): StockMkData {
  shippedStock ??= stockMkDataFrom(hullsJson.hulls, loadHullLines().sizes)
  return shippedStock
}

/**
 * M10 P5a5a: the Mk a new ship of def `defId` ships with: its hull row's `stockFit.mk`, else its band's `stockMk`
 * only when the row has a `fit` (so the Danube's stays the start fit until W6 writes its `fit`), else null (the
 * stock fit as given).
 */
export function stockMkOf(defId: string, data: StockMkData = stockMkData()): ModuleMk | null {
  const hull = hullOfDef(defId)
  const row = hull === null ? undefined : data.hulls.get(hull)
  if (row === undefined) return null
  if (row.mk !== undefined) return row.mk
  if (!row.rated || row.band === undefined) return null
  return data.stockMk[row.band] ?? null
}

/**
 * The slots the stock Mk raises: the hull-rated ones (`hullFit.ts RatedSlot`). The others keep the start fit: a whole
 * Mk II start fit draws 18 of a Mk II plant's 16 (`hangar.stockMk.test.ts`).
 */
const STOCK_MK_SLOTS: readonly RatedSlot[] = ['cargo', 'shield', 'power']

/** M10 P5a5a: the stock fit of a new `defId`: each rated module of `stockFit` at `stockMkOf` (a fresh copy either way). */
export function stockFitFor(defId: string, stockFit: Readonly<Record<SlotId, ModuleId>>, data: StockMkData = stockMkData()): Record<SlotId, ModuleId> {
  const out = { ...stockFit }
  const mk = stockMkOf(defId, data)
  if (mk === null) return out
  for (const slot of STOCK_MK_SLOTS) out[slot] = moduleId(parseModuleId(stockFit[slot]).kind, mk)
  return out
}

let cradleData: CradleData | null = null

/**
 * M10 P5a11a: whether def `defId`'s asset has an S `DOCK_CRADLE` socket (`docking/cradle.ts cradleSocketOf`), so its
 * new hangar entry gets an empty `cradle` key and `stowInCarrier` can use it (a Gannet bought at W6). `data`:
 * tests pass their own; default the shipped ships and asset manifest (parsed once). Event-rate.
 */
export function defHasCradle(defId: string, data?: CradleData): boolean {
  if (data === undefined) {
    if (cradleData === null) cradleData = { ships: shipsJson.ships, assets: assetsFileSchema.parse(assetsJson) }
    data = cradleData
  }
  return cradleSocketOf(defId, data) !== null
}

/**
 * A new delivered ship's entry: the stock fit at the hull's stock Mk (M10 P5a5a, `stockFitFor`), empty locker,
 * uninsured, no escape pod, an empty `cradle` on a hull with one (P5a11a). `cond` is a registered claimed hull's condition (M9 CH-8: the worker's
 * `flaws`/`valueMult`); omitted or pristine = a bought ship. `stock`: the stock-Mk data (tests pass copies).
 */
export function newOwnedShip(
  fleetId: string,
  defId: string,
  stockFit: Readonly<Record<SlotId, ModuleId>>,
  cond?: HullCondition,
  stock: StockMkData = stockMkData(),
): OwnedShip {
  const entry: OwnedShip = { fleetId, defId, fitted: stockFitFor(defId, stockFit, stock), locker: [], insured: false, escapePod: false }
  // M10 P5a11a: a hull with an S cradle starts with it empty.
  return withCondition(defHasCradle(defId) ? { ...entry, cradle: { carried: null } } : entry, cond)
}

/**
 * Adds `entry` to the hangar. Refuses a duplicate fleet id, the reserved id and a full fleet (`cap` fleet ships;
 * the parked lease ship, P24, never counts).
 */
export function addOwnedShip(career: Career, entry: OwnedShip, cap: number): CmdResult<Career> {
  if (entry.fleetId === OWN_SHIP_ID) return fail('invalidAmount', `"${OWN_SHIP_ID}" is the Career's own ship.`)
  const h = hangarOf(career)
  if (ownedShip(career, entry.fleetId) !== null) return fail('invalidAmount', `Ship ${entry.fleetId} is already in the hangar.`)
  let fleet = 0
  for (const s of h) if (s.fleetId !== OWN_SHIP_ID) fleet++
  if (fleet >= cap) return fail('serviceUnavailable', `The fleet is full (${cap} ships).`)
  return ok({ ...career, hangar: [...h, entry] })
}

/** Replaces the entry for `fleetId` with `change(entry)`; the career unchanged when there is none. */
export function updateOwnedShip(career: Career, fleetId: string, change: (entry: OwnedShip) => OwnedShip): Career {
  const h = career.hangar
  if (h === undefined) return career
  const i = h.findIndex((s) => s.fleetId === fleetId)
  if (i < 0) return career
  const next = h.slice()
  next[i] = change(h[i])
  return { ...career, hangar: next }
}

/** Removes the entry for `fleetId` (a total loss or a sale); a flown ship falls back to the lease ship. */
export function removeOwnedShip(career: Career, fleetId: string): Career {
  const h = career.hangar
  if (h === undefined || !h.some((s) => s.fleetId === fleetId)) return career
  const next: Career = { ...career, hangar: h.filter((s) => s.fleetId !== fleetId) }
  if (next.flownShip === fleetId) delete next.flownShip
  return next
}

/**
 * M9 (CH-7s): the Career side of a registered claim, in one write: the claimed ship's hangar entry (`entry`, from
 * the worker's `createdId`, with the hull's condition) and hulk `hulkKey` finished (its site dropped, the key in
 * `career.hulks.done`). Idempotent, so a refused write can be retried after the worker registered: an entry that is
 * already there and a key already done are left as they are. Refused only when the hangar can't take the entry.
 */
export function addClaimedShip(career: Career, hulkKey: string, entry: OwnedShip, cap: number): CmdResult<Career> {
  let next = career
  if (ownedShip(career, entry.fleetId) === null) {
    const added = addOwnedShip(career, entry, cap)
    if (!added.ok) return added
    next = added.value
  }
  const state = next.hulks ?? { done: [], sites: {} }
  if (state.done.includes(hulkKey)) return ok(next)
  const sites = Object.fromEntries(Object.entries(state.sites).filter(([k]) => k !== hulkKey))
  return ok({ ...next, hulks: { done: [...state.done, hulkKey], sites } })
}

/** Reconciles the hangar with the worker's fleet:adds a stock entry for each new ship id (owned → in hangar). */
export function syncHangar(
  career: Career,
  ships: readonly { id: string; hull: string }[],
  defOf: (hull: string) => string | null,
  stockFit: Readonly<Record<SlotId, ModuleId>>,
  cap: number,
): Career {
  let next = career
  for (const s of ships) {
    if (ownedShip(next, s.id) !== null) continue
    const defId = defOf(s.hull)
    if (defId === null) continue
    const r = addOwnedShip(next, newOwnedShip(s.id, defId, stockFit), cap)
    if (r.ok) next = r.value
  }
  return next
}

/** M10 P5a3b: `history` with `event` appended, the oldest dropped past `HULL_HISTORY_MAX` (a fresh array). */
export function withHullEvent(history: readonly HullEvent[] | undefined, event: HullEvent): HullEvent[] {
  const h = history ?? []
  const keep = h.length >= HULL_HISTORY_MAX ? h.slice(h.length - HULL_HISTORY_MAX + 1) : h.slice()
  // The schema's day is a whole non-negative faction day.
  keep.push({ ...event, day: Math.max(0, Math.floor(event.day)) })
  return keep
}

/**
 * M10 P5a3b (P5a11a calls it too): records `event` on owned ship `fleetId`'s history. The career unchanged when the
 * hangar has no such entry (the lease ship keeps no history).
 */
export function recordHullEvent(career: Career, fleetId: string, event: HullEvent): Career {
  return updateOwnedShip(career, fleetId, (e) => ({ ...e, history: withHullEvent(e.history, event) }))
}

/** M10 P5a3b: the player's name for owned ship `fleetId` (the worker's `renameShip` checked it). */
export function setOwnedShipName(career: Career, fleetId: string, name: string): Career {
  return updateOwnedShip(career, fleetId, (e) => ({ ...e, name }))
}

/** M10 P5a3b: a typed ship name, trimmed; null when empty or over `SHIP_NAME_MAX_CHARS` (the field's check). */
export function cleanShipName(raw: string): string | null {
  const t = raw.trim()
  return t.length === 0 || t.length > SHIP_NAME_MAX_CHARS ? null : t
}
